import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { PortalPage, StatCard, Section, EmptyState, Pill } from "../../components/portal/PortalPage";
import { usePortal } from "../../components/portal/usePortal";
import { useTeamLabel } from "../../components/portal/PortalBrand";
import { fmtDate, fmtDateTime, fmtMoney, REQUEST_STATUS, JOB_STATUS, EMERGENCY_STATUS, INVOICE_STATUS, PROJECT_STATUS, URGENCY } from "../../components/portal/portalUtils";
import { ClipboardText, Briefcase, Lightning, Receipt, CheckCircle, Plus, CalendarBlank, ArrowRight, Buildings, WarningCircle, Kanban } from "@phosphor-icons/react";
import { StatsGridSkeleton, CardListSkeleton } from "../../components/Skeletons";
import { PortalTourOffer } from "../../components/TourOffers";
import { SubClientsPanel } from "../../components/portal/SubClientsPanel";
import { ForClientChip, FamilyFilter, useFamilyFilter } from "../../components/portal/FamilyFilter";

const KIND = {
  request: { icon: ClipboardText, path: "/portal/requests", map: REQUEST_STATUS, cls: "text-blue-500" },
  job: { icon: Briefcase, path: "/portal/jobs", map: JOB_STATUS, cls: "text-emerald-500" },
  emergency: { icon: Lightning, path: "/portal/emergencies", map: EMERGENCY_STATUS, cls: "text-red-500" },
  project: { icon: Kanban, path: "/portal/projects", map: PROJECT_STATUS, cls: "text-violet-500" },
};

export default function ClientDashboardPage() {
  const { me, perms, client } = usePortal();
  const teamLabel = useTeamLabel();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const fam = useFamilyFilter();  // main clients: focus the live feed on one sub-client (?client=…)
  const navigate = useNavigate();

  useEffect(() => {
    API.get("/client-hub/dashboard").then(({ data }) => setData(data)).catch(() => setData({ summary: {}, active_items: [], awaiting_approval: [], upcoming: [], recent_invoices: [] }));
  }, []);

  const s = data?.summary || {};
  const items = (data?.active_items || []).filter((i) => (filter === "all" || i.kind === filter) && fam.matches(i));
  const upcoming = (data?.upcoming || []).filter(fam.matches);
  const focusName = fam.selected ? (fam.selected === "__self__" ? client?.display_name : fam.subs.find((x) => x.id === fam.selected)?.display_name) : "";
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <PortalPage
      title={`${greet}, ${me?.user?.name?.split(" ")[0] || "there"}`}
      subtitle={client ? `${client.display_name} · Client Portal` : "Client Portal"}
      testid="client-dashboard"
      actions={perms.can_request && (
        <>
          <Button variant="destructive" size="sm" onClick={() => navigate("/portal/emergencies/new")} data-testid="dash-new-emergency"><Lightning size={14} className="mr-1" weight="fill" /> Report Emergency</Button>
          <Button size="sm" onClick={() => navigate("/portal/requests/new")} data-testid="dash-new-request"><Plus size={14} className="mr-1" /> New Request</Button>
        </>
      )}
    >
      <PortalTourOffer slug="cp-welcome" blurb="Report a problem, follow the work, approve quotes and find your invoices — narrated." />
      {!data ? <StatsGridSkeleton cols={4} /> : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <StatCard icon={ClipboardText} label="Open Requests" value={s.open_requests ?? 0} color="text-blue-500" href="/portal/requests" testid="stat-open-requests" />
          <StatCard icon={Briefcase} label="Active Jobs" value={s.active_jobs ?? 0} color="text-emerald-500" href="/portal/jobs" testid="stat-active-jobs" />
          <StatCard icon={Kanban} label="Live Projects" value={s.active_projects ?? 0} color="text-violet-500" href="/portal/projects" testid="stat-active-projects" sub={s.project_requests ? `${s.project_requests} request${s.project_requests > 1 ? "s" : ""} being quoted` : undefined} />
          <StatCard icon={Lightning} label="Live Emergencies" value={s.active_emergencies ?? 0} color="text-red-500" href="/portal/emergencies" testid="stat-emergencies" />
          <StatCard icon={CheckCircle} label="Awaiting Approval" value={s.awaiting_approval ?? 0} color="text-purple-500" href="/portal/quotes" testid="stat-awaiting-approval" />
        </div>
      )}

      {data?.awaiting_approval?.length > 0 && perms.can_approve && (
        <div className="mb-6 border border-purple-300 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/30 rounded-lg p-4" data-testid="approval-banner">
          <div className="flex items-start gap-3">
            <WarningCircle size={22} className="text-purple-600 shrink-0" weight="duotone" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{data.awaiting_approval.length} item{data.awaiting_approval.length > 1 ? "s" : ""} need{data.awaiting_approval.length > 1 ? "" : "s"} your decision</p>
              <ul className="mt-2 space-y-1">
                {data.awaiting_approval.slice(0, 4).map((a) => (
                  <li key={`${a.kind}-${a.id}`}>
                    <Link to={a.kind === "request" ? `/portal/requests/${a.id}` : a.kind === "variation" && a.job_id ? `/portal/jobs/${a.job_id}` : "/portal/quotes"} className="text-xs text-purple-800 dark:text-purple-300 hover:underline flex items-center gap-1" data-testid={`approval-item-${a.id}`}>
                      <ArrowRight size={12} /> {a.reference} — {a.title}{a.amount ? ` (${fmtMoney(a.amount)})` : ""}{a.kind === "variation" ? " · additional works" : ""}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate("/portal/quotes")} data-testid="approval-banner-btn">Review</Button>
          </div>
        </div>
      )}

      {client?.is_main && <div className="mb-6"><SubClientsPanel /></div>}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section
            title={`Active Work${data ? ` (${items.length})` : ""}`}
            testid="active-work"
            action={
              <div className="flex flex-wrap items-center gap-2">
                <FamilyFilter fam={fam} />
                <div className="flex gap-1">
                {["all", "request", "job", "project", "emergency"].map((k) => (
                  <button key={k} onClick={() => setFilter(k)} className={`px-2 py-0.5 rounded text-[11px] font-medium capitalize ${filter === k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`} data-testid={`active-filter-${k}`}>
                    {k === "all" ? "All" : k === "request" ? "Requests" : k === "job" ? "Jobs" : k === "project" ? "Projects" : "Emergencies"}
                  </button>
                ))}
                </div>
              </div>
            }
          >
            {!data ? <CardListSkeleton rows={4} showHeader={false} /> : items.length === 0 ? (
              <EmptyState icon={Briefcase} title={focusName ? `Nothing in progress for ${focusName}` : "Nothing in progress"} text={focusName ? "Clear the client filter to see all live work, or raise a request for them." : "Raise a request and we'll take it from there."} action={perms.can_request && <Button size="sm" onClick={() => navigate(fam.selected && fam.selected !== "__self__" ? `/portal/requests/new?for=${fam.selected}` : "/portal/requests/new")}>New Request</Button>} />
            ) : (
              <ul className="divide-y divide-border -mx-4 -my-4">
                {items.map((it) => {
                  const k = KIND[it.kind];
                  const Icon = k.icon;
                  return (
                    <li key={`${it.kind}-${it.id}`}>
                      <Link to={`${k.path}/${it.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors" data-testid={`active-item-${it.id}`}>
                        <div className={`h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0 ${k.cls}`}><Icon size={18} weight="duotone" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-mono text-muted-foreground">{it.reference}</span>
                            <ForClientChip name={it.for_client} clientId={it.client_id} />
                            {it.mandate && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 font-semibold">MANDATE</span>}
                            {it.urgency && <Pill map={URGENCY} value={it.urgency} />}
                          </div>
                          <p className="text-sm font-medium truncate">{it.title}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{it.address || "—"}{it.supplier ? ` · ${it.supplier}` : ""}{it.stage ? ` · ${it.stage}` : ""}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <Pill map={k.map} value={it.status} />
                          <p className="text-[10px] text-muted-foreground mt-1">{it.scheduled_start ? fmtDate(it.scheduled_start) : fmtDate(it.created_at)}</p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>

          <Section title="Recent Invoices" testid="recent-invoices" action={<Link to="/portal/invoices" className="text-xs text-primary hover:underline">View all</Link>}>
            {!data ? <CardListSkeleton rows={2} showHeader={false} /> : data.recent_invoices.length === 0 ? <p className="text-xs text-muted-foreground">No invoices yet.</p> : (
              <ul className="divide-y divide-border -mx-4 -my-4">
                {data.recent_invoices.map((inv) => (
                  <li key={inv.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`recent-invoice-${inv.id}`}>
                    <Receipt size={16} className="text-amber-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate flex items-center gap-1.5">{inv.reference || inv.invoice_number} · {inv.title || inv.job_title || ""} <ForClientChip name={inv.for_client} clientId={inv.client_id} /></p>
                      <p className="text-[10px] text-muted-foreground">{fmtDate(inv.created_at)}{inv.due_date ? ` · due ${fmtDate(inv.due_date)}` : ""}</p>
                    </div>
                    <span className="text-sm font-semibold font-[Chivo]">{fmtMoney(inv.amount)}</span>
                    <Pill map={INVOICE_STATUS} value={inv.status} />
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Upcoming Visits" testid="upcoming-visits" action={<Link to="/portal/calendar" className="text-xs text-primary hover:underline flex items-center gap-1"><CalendarBlank size={12} /> Calendar</Link>}>
            {!data ? <CardListSkeleton rows={2} showHeader={false} /> : upcoming.length === 0 ? <p className="text-xs text-muted-foreground">{focusName ? `No visits scheduled for ${focusName}.` : "No visits scheduled."}</p> : (
              <ul className="space-y-3">
                {upcoming.map((u) => (
                  <li key={u.id}>
                    <Link to={`/portal/jobs/${u.id}`} className="flex gap-3 group" data-testid={`upcoming-${u.id}`}>
                      <div className="text-center shrink-0 w-11 rounded-md border border-border bg-muted/40 py-1">
                        <p className="text-[9px] uppercase text-muted-foreground">{new Date(u.scheduled_start).toLocaleDateString("en-GB", { month: "short" })}</p>
                        <p className="text-base font-bold font-[Chivo] leading-none">{new Date(u.scheduled_start).getDate()}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate group-hover:text-primary">{u.title}</p>
                        {u.for_client && <p className="mt-0.5"><ForClientChip name={u.for_client} clientId={u.client_id} /></p>}
                        <p className="text-[10px] text-muted-foreground truncate">{fmtDateTime(u.scheduled_start).split(", ")[1] || ""} · {u.supplier || (u.contractor_assigned ? teamLabel.assigned : teamLabel.tbc)}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{u.address}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Account Snapshot" testid="account-snapshot">
            <dl className="space-y-2 text-xs">
              <div className="flex justify-between"><dt className="text-muted-foreground">Completed jobs</dt><dd className="font-semibold">{s.completed_jobs ?? 0}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Total invoiced</dt><dd className="font-semibold">{fmtMoney(s.total_invoiced)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Paid</dt><dd className="font-semibold text-emerald-600">{fmtMoney(s.paid)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Outstanding</dt><dd className={`font-semibold ${s.outstanding > 0 ? "text-amber-600" : ""}`}>{fmtMoney(s.outstanding)}</dd></div>
            </dl>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Link to="/portal/properties" className="text-[11px] flex items-center gap-1 text-primary hover:underline" data-testid="snapshot-properties"><Buildings size={12} /> Properties</Link>
              <Link to="/portal/reports" className="text-[11px] flex items-center gap-1 text-primary hover:underline" data-testid="snapshot-reports"><ArrowRight size={12} /> Reports</Link>
            </div>
          </Section>
        </div>
      </div>
    </PortalPage>
  );
}
